import { ChangeDetectorRef, Component, ElementRef, OnInit } from "@angular/core";
import { DecimalPipe } from "@angular/common";

import { HomologacionConsultaService } from "../homologacion-consulta.service";
import { ComparativaComponent } from "../comparativa/comparativa.component";
import { ResultadoResumidoComponent } from "../resultado-resumido/resultado-resumido.component";
import { coletilla, dictamenClase, dictamenTexto, num, veredictoTexto } from "../homologacion-formato";
import {
  HomologacionComparado,
  HomologacionConsultaRes,
  HomologacionFicha,
  HomologacionResumen,
} from "../../shared/model/iso42001/homologacion-modelo.model";

type Pestana = "comparativa" | "modelos" | "resultado";

/**
 * Comparativa de Modelos IA (consulta): la comparativa y una lista de
 * modelos que abre el Resultado resumido. Sin «Qué se evaluó», sin
 * repeticiones, sin evidencia y sin descarga: el servidor no los manda a la
 * consulta (spec 6.6).
 *
 * La ruta, admin/homologacion-modelos/consulta, es también la llave con la
 * que el servidor autoriza (HomologacionBuss.CONSULTA); la de gestión también
 * entra.
 */
@Component({
  selector: "app-homologacion-consulta",
  standalone: true,
  imports: [DecimalPipe, ComparativaComponent, ResultadoResumidoComponent],
  templateUrl: "./homologacion-consulta.component.html",
  styleUrls: ["../homologacion-comun.css"],
})
export class HomologacionConsultaComponent implements OnInit {
  readonly POR_PAGINA = 5;

  readonly num = num;
  readonly veredictoTexto = veredictoTexto;
  readonly dictamenTexto = dictamenTexto;
  readonly dictamenClase = dictamenClase;
  readonly coletilla = coletilla;

  pestana: Pestana = "comparativa";

  modelos: HomologacionComparado[] = [];
  cargandoModelos = false;
  errorModelos = "";
  pagina = 1;
  totalPaginas = 1;
  modelosPagina: HomologacionComparado[] = [];

  resumen: HomologacionResumen | null = null;
  cargandoResumen = false;
  errorResumen = "";
  /** La ficha del catálogo vigente del modelo del resumen abierto; null si no tiene (spec 5.8). */
  ficha: HomologacionFicha | null = null;
  private pedidoResumen = 0;

  constructor(
    private service: HomologacionConsultaService,
    private host: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.cargarModelos();
  }

  irA(p: Pestana): void {
    if (p === "resultado" && !this.hayResultado) {
      return;
    }
    this.pestana = p;
  }

  get hayResultado(): boolean {
    return !!this.resumen || this.cargandoResumen || !!this.errorResumen;
  }

  cargarModelos(): void {
    this.cargandoModelos = true;
    this.errorModelos = "";
    this.service.modelos().subscribe({
      next: (res) => {
        this.cargandoModelos = false;
        if (res.codigoError) {
          this.errorModelos = this.mensaje(res, "No se pudieron consultar los modelos.");
          this.pinta();
          return;
        }
        this.modelos = res.modelos || [];
        this.paginar();
        this.pinta();
      },
      error: () => {
        this.cargandoModelos = false;
        this.errorModelos = "No se pudieron consultar los modelos: sin conexión con el servidor.";
        this.pinta();
      },
    });
  }

  irAPagina(n: number): void {
    if (n < 1 || n > this.totalPaginas || n === this.pagina) {
      return;
    }
    this.pagina = n;
    this.paginar();
  }

  private paginar(): void {
    this.totalPaginas = Math.max(1, Math.ceil(this.modelos.length / this.POR_PAGINA));
    this.pagina = Math.min(Math.max(1, this.pagina), this.totalPaginas);
    const inicio = (this.pagina - 1) * this.POR_PAGINA;
    this.modelosPagina = this.modelos.slice(inicio, inicio + this.POR_PAGINA);
  }

  get paginas(): number[] {
    const l: number[] = [];
    for (let n = 1; n <= this.totalPaginas; n++) {
      l.push(n);
    }
    return l;
  }

  /** El Resultado resumido de una evaluación: de la comparativa o de la lista. */
  abrirResumen(idEval: number): void {
    // La última que se pidió es la que se ve: si antes llega otra, se ignora.
    const pedido = ++this.pedidoResumen;
    this.resumen = null;
    this.ficha = null;
    this.cargandoResumen = true;
    this.errorResumen = "";
    this.pestana = "resultado";
    setTimeout(() => this.host.nativeElement.scrollIntoView({ block: "start" }));
    this.service.resultado(idEval).subscribe({
      next: (res) => {
        if (pedido !== this.pedidoResumen) {
          return;
        }
        this.cargandoResumen = false;
        if (res.codigoError || !res.resumen) {
          this.errorResumen = this.mensaje(res, "No se pudo abrir el resultado.");
          this.pinta();
          return;
        }
        this.resumen = res.resumen;
        this.cargarFicha(res.resumen.idModelo, pedido);
        this.pinta();
      },
      error: () => {
        if (pedido !== this.pedidoResumen) {
          return;
        }
        this.cargandoResumen = false;
        this.errorResumen = "No se pudo abrir el resultado: sin conexión con el servidor.";
        this.pinta();
      },
    });
  }

  /** Sin ficha, o si falla: el resumen se ve igual, sin el bloque. */
  private cargarFicha(idModelo: number, pedido: number): void {
    this.service.ficha(idModelo).subscribe({
      next: (res) => {
        if (pedido === this.pedidoResumen && !res.codigoError) {
          this.ficha = res.ficha || null;
          this.pinta();
        }
      },
    });
  }

  /** La app corre sin zonas: una respuesta HTTP no repinta sola. */
  private pinta(): void {
    this.cdr.markForCheck();
  }

  private mensaje(res: HomologacionConsultaRes | null | undefined, porOmision: string): string {
    if (res && res.codigoError === "AUTH-001") {
      return "Tu sesión caducó. Vuelve a entrar.";
    }
    return (res && res.mensajeError) || porOmision;
  }
}
